
    import TransactionService from '@/service/TransactionService';
    import PortfolioService from '@/service/PortfolioService';
    import FormatterService from "@/service/FormatterService";
    import Alert from "@/components/Alert";
    import QuoteService from "../../service/QuoteService";
    import { backendExceptionHandler, companyLogoHandler } from '@/mixins';
    import { transactionTypes } from "@/constants/transaction"

    const DATE_REGEX = /^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/
    export default {
        name: 'AddTransactionToPosition',
        mixins: [backendExceptionHandler, companyLogoHandler],
        components: {Alert},

        props: {
            portfolioId: {
                type: String,
                default: null,
                optional: true,
            },
            portfolios: {
                type: Array,
                default: () => [],
                optional: true,
            },
            allocation: {
                type: Array,
                optional: true,
                default: () => [],
            },
            interval: String,
            isAssetPortfolio: {
                type: Boolean,
                default: false
            },
            assetGroupKey: {
                type: String,
                default: ''
            },
            dialogKey: {
                type: String,
                default: null
            },
        },

        data() {
            return {
                menu: false,
                transactionId: null,
                isVisible: false,
                isLoading: false,
                isLoadingPrepareData: false,
                isLoadingSubmitButton: false,
                isLoadingPrice: false,
                isLivePortfolio: false,
                qty: null,
                date: null,
                pickerDate: null,
                price: null,
                type: transactionTypes.buy,
                name: null,
                country: null,
                isin: null,
                logo: null,
                mic: 'XFRA',
                mics: [],
                currency: null,
                errorMessage: null,
                instrumentType: null,
                targetPortfolio: null,
                totalQty: null,
                isLoadingTotalQty: null,
                broker: null,
                costs: null,
                taxes: null,
                brokers: [],
                watchOnly: true,
                currencies: [],
                isCreateNewTransactionByShareDetails: false,
                costsCurrency: '',
                taxesCurrency: '',
                isShowCostsAndTaxes: false,
                importSource: ''
            }
        },

        computed: {
            getMicLogo() {
                const currentMic = this.mics.filter(mic => mic.mic === this.mic)
                if(currentMic.length) {
                    return currentMic[0].logo
                }
                return 'XFRA.png'
            },
            quantityRules() {
                if (this.watchOnly) {
                    return []
                }
                return [ this.rules.isNumeric, this.rules.isNotZero, this.rules.isValidNumber ]
            },
            dateRules() {
                if (this.watchOnly) {
                    return []
                }
                return [ this.rules.isValidDate ]
            },
            priceRules() {
                if (this.watchOnly) {
                    return []
                }
                return [this.rules.required, this.rules.isValidCurrencyField(this.price, this.currency), this.rules.isNotNegative(this.price)]
            },
            nonEmptyAllocation() {
                return this.allocation.reduce(
                    (prev, curr) => curr.qty ? [...prev, curr.name] : prev,
                    [],
                )
            },
            isDetailsPage() {
                return this.$route?.matched.some(match => 
                    match?.path == '/aktie/'
                )
            },
            portfolioOptions() {
                if (this.isDetailsPage) {
                    return this.portfolios?.filter(p => p.portfolioType !== 'asset')
                }

                return this.portfolios
            },
            assetMethodLogoName() {
                if (!this.isAssetPortfolio || !this.assetGroupKey) {
                    return ''
                }
                
                const assetMethodsIcons = this.format.getAssetMethodIcons()
                return assetMethodsIcons[this.assetGroupKey]
            },
            isPrivacyEnabled() {
                return this.$store.state.preferences.isPrivacyEnabled
            },
            buySellOptions() {
                return [
                    {
                        text: this.$t('component.transaction.addTransactionToPosition.buy'),
                        value: transactionTypes.buy
                    },
                    {
                        text: this.$t('component.transaction.addTransactionToPosition.sell'),
                        value: transactionTypes.sell
                    }
                ]
            },
            taxesRules() {
                return [this.rules.isValidCurrencyField(this.taxes, this.taxesCurrency)]
            },
            costsRules() {
                return [this.rules.isValidCurrencyField(this.costs, this.costsCurrency)]
            },
            dialogWidth() {
                if (!this.$vuetify.breakpoint.mdAndUp) {
                    return '100vw'
                }

                return '650px'
            }
        },
        methods: {
            async getPositionId() {
                if (this.positionId) {
                    return this.positionId
                }
                await this.addPositionToPortfolio()

                return this.positionId
            },
            async save() {
                this.watchOnly = false
                await this.$nextTick()
                if (!this.$refs.form.validate()) {
                    return
                }

                const qty = this.formatQty()
                this.positionId = await this.getPositionId()
                this.isLoadingSubmitButton = true
                try {
                    await this.transactionService.save({
                        transactionId: this.transactionId,
                        portfolioId: this.targetPortfolio ?? this.transactionPortfolioId,
                        positionId: this.positionId,
                        qty: qty,
                        mic: this.mic,
                        currency: this.currency,
                        price: this.format.convertNumericToFloat(this.price),
                        date: this.format.germanToIsoDate(this.date) + 'T00:00:00+0',
                        broker: this.broker,
                        taxes: this.type === transactionTypes.sell ? this.format.convertNumericToFloat(this.taxes, null) : null,
                        costs: this.format.convertNumericToFloat(this.costs, null),
                        taxesCurrency: this.taxesCurrency,
                        costsCurrency: this.costsCurrency,
                    }, this.$t)

                    this.$root.$emit('requestPortfolioPositionRendering')
                    this.$root.$emit('request-portfolios-refresh')
                    this.$root.$emit('kpi-totals-data-updated')
                    this.$root.$emit('transactions-load')
                    this.$root.$emit('recall-portfolio-tabs')
                    this.$root.$emit('request-performance-refresh')
                    this.$emit(
                        'position-upserted',
                        this.targetPortfolio ?? this.transactionPortfolioId,
                    )
                    this.closeWindow()
                    const action = 'sp_portfolio_position_order_' + (qty > 0 ? 'buy' : 'sell')
                    this.$gtag?.event(action, {value: 1})
                } catch (e) {
                    this.handleBackendException(e, this.$t('component.transaction.addTransactionToPosition.transactionError'))
                } finally {
                    this.isLoadingSubmitButton = false
                }
            },

            formatQty() {
                const qty = this.format.convertNumericToFloat(this.qty)
                if (this.type === transactionTypes.sell) {
                    return -Math.abs(qty)
                }
                return qty
            },

            isValidDate(value) {
                if(!value) return this.$t('component.transaction.addTransactionToPosition.noDate')
                if (value.indexOf('.')) value = this.format.germanToIsoDate(value)
                value = value.replace(/\s/g, '');
                let givenDate
                try {
                    if (!DATE_REGEX.exec(value)) throw 'error'
                    givenDate = new Date(value)
                } catch (e) {
                    return this.$t('component.transaction.addTransactionToPosition.invalidDate')
                }
                if (givenDate > new Date()) {
                    return this.$t('component.transaction.addTransactionToPosition.futureDate')
                }
                return true
            },

            closeWindow() {
                this.isVisible = false
            },

            reset() {
                this.importSource = ''
                this.isLivePortfolio = false
                this.transactionId = null
                this.qty = null
                this.type = transactionTypes.sell
                this.name = null
                this.isin = null
                this.logo = null
                this.mic = 'XFRA'
                this.currency = null
                this.positionId = null
                this.instrumentType = null
                this.price = null
                this.errorMessage = null
                this.totalQty = null
                this.taxes = null
                this.costs = null
                this.broker = null
                this.taxesCurrency = ''
                this.costsCurrency = ''
                this.watchOnly = true
                this.isShowCostsAndTaxes = false
                if (this.$refs.form) {
                    this.$refs.form.resetValidation()
                }
            },

            async prepareDataInit() {
                try{
                    this.isLoadingPrepareData = true;
                    await this.getBrokers()
                    await this.prepareMics()
                    await this.prepareCurrencies();
                } catch (error) {
                    console.log('@@Error: ', error);
                } finally {
                    this.isLoadingPrepareData = false;
                }
            }, 

            focusQty() {
                // use nextTick to avoid async binding
                this.$nextTick(() => {
                    this.$refs.inputQtyTransaction.$el.querySelector('input').select()
                })
            },

            async openWindow(
                transaction,
                totalQty = null,
                type = null,
                isLivePortfolio = false,
                dialogKeyToCheck = null
            ) {
                if (this.dialogKey && !dialogKeyToCheck || this.dialogKey !== dialogKeyToCheck) {
                    return;
                }
                if (!this.portfolios?.length) {
                    this.$root.$emit('request-portfolios-refresh')
                }
                this.reset() 
                this.isLivePortfolio = isLivePortfolio
                this.isVisible = true
                await this.prepareDataInit()
                this.date = this.format.date({date: new Date})

                if (!this.portfolioId) {
                    this.transactionPortfolioId = transaction.portfolioId
                }

                this.instrumentType = transaction.instrumentType
                this.name = transaction.name
                this.isin = transaction.isin
                this.logo = transaction.logo
                this.type = transaction.type
                this.mic = transaction.mic
                this.country = transaction.countryCode
                this.currency = transaction.currency
                this.positionId = transaction.positionId
                this.isCreateNewTransactionByShareDetails = !transaction.positionId

                this.price = this.toNumericInput(transaction.purchasePrice)

                if (!this.type && !type) {
                    if (transaction.qty >= 0) {
                        this.type = transactionTypes.buy;
                    } else {
                        this.type = transactionTypes.sell;
                    }
                } else if (!this.type && type) {
                    this.type = type
                }
                if (transaction.transactionId) {
                    this.transactionId = transaction.transactionId
                    // brokerId, taxes and costs are currently missing in the input transaction --get them:
                    try {
                        const fullTransaction = await this.transactionService.getTransaction(
                            transaction.portfolioId, transaction.positionId, transaction.transactionId)
                        this.broker = fullTransaction.brokerId
                        this.taxes = this.toNumericInput(fullTransaction.taxes, null)
                        this.costs = this.toNumericInput(fullTransaction.costs, null)
                        this.taxesCurrency = fullTransaction.taxesCurrency
                        this.costsCurrency = fullTransaction.costsCurrency
                        this.importSource = fullTransaction.importSource
                        // in edit mode, show costs and taxes row if there is taxes or costs
                        this.isShowCostsAndTaxes = this.taxes || this.costs
                    } catch (e) {
                        this.handleBackendException(e, this.$t('component.transaction.addTransactionToPosition.transactionNotRetrieved'))
                    }
                } else {
                    // in create mode, taxesCurrency and costsCurrency should be set as position's currency 
                    if (transaction?.positionId && transaction?.currency) {
                        this.taxesCurrency = transaction.currency ||  ''
                        this.costsCurrency = transaction.currency ||  ''
                    }

                    this.fetchPriceForDate()
                }
                if(transaction.purchaseDate) {
                    this.date = this.format.date({date: new Date(transaction.purchaseDate)})
                }
                if(transaction.qty) {
                    this.qty = Math.abs(transaction.qty)
                    if(transaction.qty < 0)  this.type = transactionTypes.sell
                }
                if (this.qty) {
                    this.qty = this.format.autoFormat(this.qty, 'quantity')
                }
                if (this.qty) {
                    this.focusQty()
                }
                this.totalQty = totalQty
                if(totalQty === null) this.totalQty = this.getTransactionCount(transaction.positionId)
                this.pickerDate = this.date.split('.').reverse().join('-')

                const portfolioId = this.portfolioId || transaction?.portfolioId
                this.targetPortfolio = portfolioId
                    ? parseInt(portfolioId)
                    : null
                if (!this.targetPortfolio && this.nonEmptyAllocation.length == 1) {
                    const portfolio = this.portfolios.find(
                        portfolio => portfolio.name == this.nonEmptyAllocation[0]
                    )
                    if (!portfolio) {
                        return
                    }
                    this.targetPortfolio = portfolio.id
                    this.queryPortfolioPositions()
                }
            },
            async queryCurrencies() {
                this.currencies = await this.query("currency", this.$t('component.transaction.addTransactionToPosition.currencies'));
            },
            async prepareCurrencies() {
                await this.queryCurrencies();
                const groupTitles = {others: this.$t('Weitere'), top: this.$t('Häufig verwendet')};
                this.currencies = this.currencies?.filter(c => c.isActive)
                this.currencies = this.format.getFormattedCurrencies(this.currencies, groupTitles)
            },

            async prepareMics() {
                if (this.mics.length) {
                    return
                }
                await this.queryMics()

                this.mics.unshift({header: this.$t('component.transaction.addTransactionToPosition.other')})
                this.moveMicToTop('XNAS')
                this.moveMicToTop('XNYS')
                this.moveMicToTop('XLON')
                this.moveMicToTop('XETR')
                this.moveMicToTop('XFRA')
                this.mics.unshift({header: this.$t('component.transaction.addTransactionToPosition.frequent')})
                this.mics.forEach(item => {
                    item.text = `${item.niceName} (${item.mic})`
                });
            },

            moveMicToTop(key) {
                const item = this.mics.find(item => item.mic === key)
                if (item) {
                    this.mics.unshift(item)
                }
            },

            async queryMics() {
                this.mics = await this.query('mic', this.$t('component.transaction.addTransactionToPosition.marketIdentifierCodes'))
            },

            async queryPortfolioPositions() {
                if (!this.targetPortfolio) {
                    return
                }
                this.isLoading = true
                this.isLoadingTotalQty = true
                try {
                    this.positions = await this.portfolioService.queryPositions(
                            this.targetPortfolio,
                            this.interval
                        )
                        .then(data => data.positions.length ? data.positions : [{}])
                    const position = this.positions.find(
                        position => position.isin == this.isin
                    )
                    if (position) {
                        this.totalQty = position.qty
                        this.positionId = position.id
                    } else {
                        this.totalQty = 0
                    }
                } catch(error) {
                    this.handleBackendException(error, this.$t('component.transaction.addTransactionToPosition.portfolioPositionsNotRetrieved'))
                } finally {
                    this.isLoading = false
                    this.isLoadingTotalQty = false
                }
            },

            async query(endpoint, endpointLabel) {
                this.isLoading = true
                return await this
                    .$axios
                    .get('/api/'+endpoint)
                    .then(response => response.data)
                    .catch(error => {
                        this.handleBackendException(error, `${this.$t('component.transaction.addTransactionToPosition.unableToRetrieve')} ${endpointLabel}: `)
                    })
                    .finally(() => this.isLoading = false)
            },

            async getTransactionCount(positionId) {
                this.isLoadingTotalQty = true
                
                try {
                    const transactions = await this.transactionService.getAllForPosition({
                        portfolioId: this.portfolioId,
                        positionId: positionId
                    })
                    if (typeof transactions[0] !== 'undefined') {
                      this.totalQty = transactions[0].positionQty
                    }
                } catch (e) {
                    this.handleBackendException(e, this.$t('component.transaction.addTransactionToPosition.transactionCountNotLoaded'))
                } finally {
                    this.isLoadingTotalQty = false
                }
            },

            async fetchPriceForDate() {
                this.isLoadingPrice = true
                try {
                    const datetime = this.format.germanToIsoDate(this.date) + 'T23:59:59'  // last second of the day
                    const quote = await this.quoteService.getHistoricQuote(
                        this.currency, this.isin, datetime
                    )
                    this.price = this.toNumericInput(quote.price)
                    this.currency = quote.currency
                } finally {
                    this.isLoadingPrice = false
                }
            },

            async watchAsset() {
                this.watchOnly = true
                await this.$nextTick()
                if (!this.$refs.form.validate()) {
                    return
                }
                await this.addPositionToPortfolio();
                if (!this.positionId) {
                    return;
                }
                this.$emit('position-upserted', this.targetPortfolio)
                this.closeWindow()
            },

            async addPositionToPortfolio() {
                this.isLoading = true
                await this
                    .$axios
                    .post('/api/portfolio/' + this.targetPortfolio + '/position', {
                        isin: this.isin, mic: this.mic, currency: this.currency,
                    })
                    .then(response => {
                        this.$root.$emit('requestPortfolioPositionRendering')
                        this.$root.$emit('request-portfolios-refresh')
                        this.positionId = response.data.id
                    })
                    .catch(error => {
                        this.handleBackendException(error, this.$t('component.transaction.addTransactionToPosition.positionNotCreated'))
                    })
                    .finally(() => {
                        this.isLoading = false
                    })

                this.$gtag?.event('sp_portfolio_position_create', {value: this.portfolios.length})
            },

            closeMenu() {
                this.date = new Intl.DateTimeFormat('DE', {
                    day: '2-digit', month: '2-digit', year: 'numeric'
                }).format(new Date(this.pickerDate))
                this.$refs.menu.save(this.date)
                this.fetchPriceForDate()
            },

            submitOnReturn(e) {
                if(e.key === "Enter" && this.isVisible) this.save()
            },

            onKeyUpDateInput(e) {
                if(e.key === "Tab") return
                if (this.isValidDate(this.date) === true) {
                    this.fetchPriceForDate()
                }
            },

            onChangeCurrency(e) {
                this.fetchPriceForDate()
            },

            toNumericInput(value) {
                if (!value) {
                    return null
                }
                return this
                    .format
                    .numberToStringWithDecimals(value, 'Import')
                    .replace('.', ',')
            },

            async getBrokers() {
                if (this.brokers.length) {
                    return
                }
                try {
                    this.brokers = await this.transactionService.getBrokers()
                } catch (e) {
                    this.handleBackendException(e, this.$t('component.transaction.addTransactionToPosition.brokerListNotRetrieved'))
                }
            },

            setRules() {
                this.rules = {
                    isNumeric: value => {
                        if (value == '' || value == null) {
                            return true;
                        }
                        if (!/^-{0,1}[0-9]{1,3}(\.?[0-9]{3})*(,[0-9]*)*$/.test(value)) {
                            return this.$t('component.transaction.addTransactionToPosition.numericField')
                        }
                        value = this.format.convertNumericToFloat(value)
                        if (isNaN(value)) return this.$t('component.transaction.addTransactionToPosition.numericField')
                        return true
                    },
                    isValidDate: value => this.isValidDate(value),
                    isNotZero: value => value != 0 || this.$t('component.transaction.addTransactionToPosition.invalidValue'),
                    requiredExceptCrypto: value => this.instrumentType?.toLowerCase() === 'crypto'
                        || !!value
                        || this.$t('component.transaction.addTransactionToPosition.requiredField'),
                    required: value => !!value || this.$t('component.transaction.addTransactionToPosition.requiredField'),
                    isNotEmpty: value => value != null && value != '' || this.$t('component.transaction.addTransactionToPosition.valueNotEmpty'),
                    isValidNumber: value => {
                        const floatValue = this.format.convertNumericToFloat(value);
                        return (floatValue >= -100000000 && floatValue <= -0.000001) || (floatValue >= 0.000001 && floatValue <= 100000000) || this.$t('component.transaction.addTransactionToPosition.numberRangeError');
                    },
                    isValidCurrencyField: (value, currency) => {
                        const isValidNumber = this.rules.isNumeric(value)

                        if (isValidNumber !== true) {
                            return isValidNumber
                        }

                        return currency != null && currency != '' || this.$t('component.transaction.addTransactionToPosition.currencyNotEmpty')
                    },
                    isNotNegative: (value) => {
                      const floatValue = this.format.convertNumericToFloat(value);
                      return floatValue >= 0 || this.$t('component.transaction.addTransactionToPosition.valueNotNegative')
                    }
                }
            },

            getCompanyLogoClass(logo) {
                return (logo && logo != null) ? 'company-logo' : 'company-logo-invalid'
            },
            updateErrorMessage() {
                this.errorMessage = ""
            },
            toggleShowCostsAndTaxes () {
                this.isShowCostsAndTaxes = !this.isShowCostsAndTaxes
            }
        },

        watch: {
            targetPortfolio() {
                this.queryPortfolioPositions()
            },
        },

        created() {
            this.transactionTypes = transactionTypes
            this.portfolioService = new PortfolioService(this.$axios)
            this.format = new FormatterService({ currency: 'EUR' })
            this.quoteService = new QuoteService(this.$axios, this.$store)
            this.transactionService = new TransactionService(this.$axios, this.$store)
            this.$root.$on("request-transaction-window", this.openWindow)
            this.setRules()
        },

        beforeMount() {
            window.addEventListener('keydown', this.submitOnReturn)
        },

        beforeDestroy() {
            this.$root.$off("request-transaction-window", this.openWindow)
            window.removeEventListener('keydown', this.submitOnReturn)
        }
    }
